/**
 * Analytics Service
 * Simple service for tracking events with Google Analytics
 * gtag.js is loaded directly in index.html
 */

declare global {
	interface Window {
		dataLayer?: Array<Record<string, unknown> | unknown[]>;
		gtag?: (
			command: string,
			targetId: string | Date,
			config?: Record<string, unknown>
		) => void;
	}
}

interface EventParams {
	event_category?: string;
	event_label?: string;
	value?: number;
	[key: string]: unknown;
}

class AnalyticsService {
	private initialized = false;
	private readonly measurementId = 'AW-17634633490';

	/**
	 * Check if user has consented to analytics cookies
	 */
	private hasConsent(): boolean {
		if (typeof window === 'undefined') return false;
		const consent = localStorage.getItem('cookie-consent');
		return consent === 'accepted';
	}

	/**
	 * Initialize analytics services
	 * gtag.js is already loaded in index.html, we just need to check consent
	 */
	initialize(): void {
		if (this.initialized) {
			return;
		}

		if (!this.hasConsent()) {
			if (
				import.meta.env.DEV ||
				import.meta.env.VITE_DEBUG_ANALYTICS === 'true'
			) {
				console.log('[Analytics] Initialization skipped - no cookie consent');
			}
			this.initialized = true;
			return;
		}

		// gtag is already loaded from index.html, just mark as initialized
		if (
			import.meta.env.DEV ||
			import.meta.env.VITE_DEBUG_ANALYTICS === 'true'
		) {
			console.log('[Analytics] Initialized - gtag.js loaded from index.html');
		}

		this.initialized = true;
	}

	/**
	 * Track page view
	 */
	trackPageView(path: string, title?: string): void {
		if (!this.hasConsent() || !window.gtag) {
			return;
		}

		window.gtag('config', this.measurementId, {
			page_path: path,
			page_title: title || document.title,
			page_location: window.location.origin + path,
		});

		window.gtag('event', 'page_view', {
			page_path: path,
			page_title: title || document.title,
			page_location: window.location.origin + path,
		});

		if (
			import.meta.env.DEV ||
			import.meta.env.VITE_DEBUG_ANALYTICS === 'true'
		) {
			console.log('[Analytics] Page view tracked', {
				path,
				title: title || document.title,
			});
		}
	}

	/**
	 * Track custom event
	 */
	trackEvent(eventName: string, params?: EventParams): void {
		if (!this.hasConsent() || !window.gtag) {
			return;
		}

		window.gtag('event', eventName, params);

		if (
			import.meta.env.DEV ||
			import.meta.env.VITE_DEBUG_ANALYTICS === 'true'
		) {
			console.log('[Analytics] Event tracked', { eventName, params });
		}
	}

	/**
	 * Track form submission
	 */
	trackFormSubmission(formName: string, formId?: string): void {
		this.trackEvent('form_submit', {
			event_category: 'engagement',
			event_label: formName,
			form_id: formId,
		});
	}

	/**
	 * Track phone number click
	 */
	trackPhoneClick(phoneNumber: string): void {
		this.trackEvent('phone_click', {
			event_category: 'contact',
			event_label: phoneNumber,
		});
	}

	/**
	 * Track email click
	 */
	trackEmailClick(email: string): void {
		this.trackEvent('email_click', {
			event_category: 'contact',
			event_label: email,
		});
	}

	/**
	 * Track external link click
	 */
	trackExternalLink(url: string, linkText?: string): void {
		this.trackEvent('external_link_click', {
			event_category: 'outbound',
			event_label: linkText || url,
			link_url: url,
		});
	}

	/**
	 * Track CTA button click
	 */
	trackCTAClick(ctaText: string, location?: string): void {
		this.trackEvent('cta_click', {
			event_category: 'engagement',
			event_label: ctaText,
			location: location,
		});
	}
}

// Export singleton instance
export const analytics = new AnalyticsService();
